import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  AddReleaseRequest, AddReleaseResponse, ListReleasesRequest, ListReleasesResponse,
  ProductName, Release, ReleaseStatus, SituViewWebApiClient, RenderReleaseStatus, UpdateReleaseRequest
} from 'situview-webapi-client';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { AuthService, ProductNameService, ReleaseService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'release-list',
  templateUrl: './release-list.component.html',
  styleUrls: ['./release-list.component.scss']
})
export class ReleaseListComponent extends SituBaseComponent {
  loading: boolean = false;
  data = new MatTableDataSource<Release>();
  filteredData: Release[] | undefined;
  productNames = ProductName;
  renderReleaseStatuses = RenderReleaseStatus;
  availableStatuses: { value: RenderReleaseStatus; name: string; color: string }[] = [];

  /* Filters */
  searchTerm: string = "";
  onlyActiveFilter: boolean | undefined = true;
  statusFilter: RenderReleaseStatus | undefined = undefined;
  /* Filters */

  /* Query Parameters */
  readonly SearchParamName: string = "search";
  readonly ReleasedParamName: string = "released";
  /* Query Parameters */

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private productNameService: ProductNameService,
    private releaseService: ReleaseService
  ) { 
    super(authService, stateService);

    if (this.route.snapshot.queryParams[this.SearchParamName]) {
      this.searchTerm = this.route.snapshot.queryParams[this.SearchParamName];
    }

    if (this.route.snapshot.queryParams[this.ReleasedParamName]) {
      this.onlyActiveFilter = true;
    }

    this.initializeAvailableStatuses();
  }

  private initializeAvailableStatuses(): void {
    this.availableStatuses = [
      {
        value: RenderReleaseStatus.PreRelease,
        name: this.getRenderStatusName(RenderReleaseStatus.PreRelease),
        color: this.getRenderStatusColor(RenderReleaseStatus.PreRelease)
      },
      {
        value: RenderReleaseStatus.InFlight,
        name: this.getRenderStatusName(RenderReleaseStatus.InFlight),
        color: this.getRenderStatusColor(RenderReleaseStatus.InFlight)
      },
      {
        value: RenderReleaseStatus.Released,
        name: this.getRenderStatusName(RenderReleaseStatus.Released),
        color: this.getRenderStatusColor(RenderReleaseStatus.Released)
      }
    ];
  }

  protected onSelectedCompanyChange(): void {
    this.loadData().then(() => {
      this.filterData();
    });
  }

  protected onThemeChange(): void {
    // Refresh available statuses when theme changes
    this.initializeAvailableStatuses();
  }

  onClickRelease(item: Release | undefined, dynamicList?: string) {
    if (dynamicList) {
      this.router.navigate(['admin/release', dynamicList]);
    } else if (item) {
      this.router.navigate(['admin/release', item.id]);
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getRenderStatusColor(status: RenderReleaseStatus): string {
    switch (status) {
      case RenderReleaseStatus.PreRelease:
        return this.isDarkMode ? '#89CFF0' : '#1E90FF'; // Baby Blue / Dodger Blue
      case RenderReleaseStatus.InFlight:
        return this.isDarkMode ? '#FFB347' : '#FF8C00'; // Pastel Orange / Dark Orange
      case RenderReleaseStatus.Released:
        return this.isDarkMode ? '#77DD77' : '#32CD32'; // Pastel Green / Lime Green
      default:
        return this.isDarkMode ? '#A9A9A9' : '#D3D3D3'; // Dark Gray / Light Gray
    }
  }

  getRenderStatusName(status: RenderReleaseStatus): string {
    switch (status) {
      case RenderReleaseStatus.PreRelease:
        return 'Pre-Release';
      case RenderReleaseStatus.InFlight:
        return 'In Flight';
      case RenderReleaseStatus.Released:
        return 'Released';
      default:
        return 'Unknown';
    }
  }


  filterByStatus(status: RenderReleaseStatus | undefined) {
    this.statusFilter = status;
    this.filterData();
  }

  // Optimized methods to avoid template method calls
  getStatusInfo(status: RenderReleaseStatus): { name: string; color: string } {
    const statusInfo = this.availableStatuses.find(s => s.value === status);
    return statusInfo ? { name: statusInfo.name, color: statusInfo.color } : 
           { name: 'Unknown', color: this.isDarkMode ? '#A9A9A9' : '#D3D3D3' };
  }

  trackByReleaseId(index: number, release: Release): string | undefined {
    return release.id;
  }

  isExpired(releaseDate: Date) {
    return releaseDate < new Date();
  }

  filterData() {
    this.filteredData = this.data.data.filter(item => {
      const lowerCaseSearch = this.searchTerm.toLowerCase();
      const matchesSearch = item.name?.toLowerCase().includes(lowerCaseSearch) || item.description?.toLowerCase().includes(lowerCaseSearch);
      const matchesStatus = this.statusFilter === undefined || item.renderReleaseStatus === this.statusFilter;
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      // Sort by priority (higher first), then by name
      const aPriority = a.priority || 0;
      const bPriority = b.priority || 0;
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    this.updateParams();
  }

  
  filterOnlyActive(onlyActive: boolean | undefined) {
    if (onlyActive != this.onlyActiveFilter) {
      this.onlyActiveFilter = onlyActive;
    }
    this.loadData().then(() => {
        this.filterData();
      });
  }

  loadData() {
    this.loading = true;

    let request = new ListReleasesRequest();
    request.companyId = this.selectedCompany?.id;
    request.includeStats = true;
    if (this.onlyActiveFilter) request.isNotReleased = true;

    return this.situViewApiClient.listReleases(request)
      .then((response: ListReleasesResponse) => {
        if (response.success) {
          // Sort by priority (higher first), then by name
          const sortedItems = (response.items ?? []).sort((a, b) => {
            const aPriority = a.priority || 0;
            const bPriority = b.priority || 0;
            if (aPriority !== bPriority) {
              return bPriority - aPriority;
            }
            return (a.name ?? '').localeCompare(b.name ?? '');
          });
          this.data.data = sortedItems;
          this.filteredData = sortedItems;
        } else {
          this.showMessage("There was an issue retrieving releases!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve releases!");
      });
  }

  addRelease() {
    const dialogRef = this.dialog.open(RenameDialogComponent, {
        width: '350px',
        height: '60%',
        data: { 
            existingName: ""
        }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(name => {
        if (name !== undefined) {
            let request = new AddReleaseRequest();
            request.companyId = this.selectedCompany?.id;
            request.name = name;
            request.description = "Release description";
            let releaseDate = new Date();
            releaseDate.setMonth(releaseDate.getMonth() + 1);
            request.releaseDate = releaseDate;

            this.situViewApiClient.addRelease(request)
                .then((response: AddReleaseResponse) => {
                    if (response.success) {
                        this.snackBar.open(`Added release!`, 'OK', { duration: 2000 });
                        //this.router.navigate(['admin/release', response.releaseId]);
                        this.loadData();
                    } else {
                        this.snackBar.open(`Error adding release!`, 'OK', { duration: 2000 });
                    }
                })
                .catch((err: any) => {
                    this.snackBar.open(`Error adding release!`, 'OK', { duration: 2000 });
                });
        }
    }));
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.searchTerm) {
      queryParams.search = this.searchTerm;
    }
    if (this.onlyActiveFilter !== undefined) {
      queryParams.released = this.ReleasedParamName;
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.searchTerm) {
      delete urlTree.queryParams[this.SearchParamName];
    }
    if (this.onlyActiveFilter === undefined) {
      delete urlTree.queryParams[this.ReleasedParamName];
    }
  
    this.router.navigateByUrl(urlTree);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  adjustPriority(release: Release, direction: 'up' | 'down'): void {
    if (!release.id) return;
    
    const currentPriority = release.priority || 0;
    let newPriority: number;
    
    // Filter out expired releases - they shouldn't affect priority calculations
    const activeReleases = this.filteredData?.filter(r => !this.isExpired(r.releaseDate || new Date()));
    
    if (direction === 'up') {
      // Find the next higher priority among active releases only
      const higherPriorityReleases = activeReleases?.filter(r => 
        (r.priority || 0) > currentPriority
      ).sort((a, b) => (a.priority || 0) - (b.priority || 0));
      
      if (higherPriorityReleases && higherPriorityReleases.length > 0) {
        // Set priority to just above the next higher one
        newPriority = (higherPriorityReleases[0].priority || 0) + 1;
      } else {
        // If no higher priority exists, increment by 10
        newPriority = currentPriority + 10;
      }
    } else {
      // Find the next lower priority among active releases only
      const lowerPriorityReleases = activeReleases?.filter(r => 
        (r.priority || 0) < currentPriority
      ).sort((a, b) => (b.priority || 0) - (a.priority || 0));
      
      if (lowerPriorityReleases && lowerPriorityReleases.length > 0) {
        // Set priority to just below the next lower one
        const nextLowerPriority = lowerPriorityReleases[0].priority || 0;
        newPriority = Math.max(0, nextLowerPriority - 1);
      } else {
        // If no lower priority exists, decrement by 10 but not below 0
        newPriority = Math.max(0, currentPriority - 10);
      }
    }

    this.updateReleasePriority(release, newPriority);
  }

  private updateReleasePriority(release: Release, newPriority: number): void {
    const request = new UpdateReleaseRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseId = release.id;
    request.name = release.name;
    request.description = release.description;
    request.releaseDate = release.releaseDate;
    request.renderReleaseStatus = release.renderReleaseStatus;
    request.situPlan = release.situPlan;
    request.situVr = release.situVr;
    request.situRender = release.situRender;
    request.situRaytracedPlus = release.situRaytracedPlus;
    request.skipRaytraced = release.skipRaytraced;
    request.priority = newPriority;

    this.situViewApiClient.updateRelease(request)
      .then(response => {
        if (response.success) {
          // Update the local data
          release.priority = newPriority;
          this.filterData();
          this.showMessage('Priority updated successfully');
        } else {
          this.showMessage('Failed to update priority');
        }
      })
      .catch(error => {
        console.error('Error updating priority:', error);
        this.showMessage('Error updating priority');
      });
  }

  canMoveUp(release: Release): boolean {
    if (!this.filteredData || this.filteredData.length === 0) return false;
    
    // Only consider active releases
    const activeReleases = this.filteredData.filter(r => !this.isExpired(r.releaseDate || new Date()));
    if (activeReleases.length <= 1) return false;
    
    // Find the index of the current release in the active releases
    const currentIndex = activeReleases.findIndex(r => r.id === release.id);
    
    // Can move up if it's not the first item
    return currentIndex > 0;
  }

  canMoveDown(release: Release): boolean {
    if (!this.filteredData || this.filteredData.length === 0) return false;
    
    // Only consider active releases
    const activeReleases = this.filteredData.filter(r => !this.isExpired(r.releaseDate || new Date()));
    if (activeReleases.length <= 1) return false;
    
    // Find the index of the current release in the active releases
    const currentIndex = activeReleases.findIndex(r => r.id === release.id);
    
    // Can move down if it's not the last item
    return currentIndex >= 0 && currentIndex < activeReleases.length - 1;
  }
}